import React from "react";
import { UserProp } from "@components/common/ddr/ddrTypes";
import {
  File,
  Database,
  AlertTriangle,
  Paperclip,
  Mail,
  DatabaseIcon,
  TriangleAlert,
  Loader2,
} from "lucide-react";
import { formatBandwidth } from "@utils/utils";
import NoData from "../NoDataLabel";

interface SourcesProps {
  userDetails: UserProp;
  loading?: boolean;
}

const Summary: React.FC<SourcesProps> = ({
  userDetails = {} as UserProp,
  loading = false,
}) => {
  const checkNoData = (data: UserProp) => {
    if (
      !data ||
      (data &&
        !data.files?.source_list?.length &&
        !data.emails?.source_list?.length &&
        !data.database?.source_list?.length &&
        !data.conversations?.source_list?.length &&
        !data.generics?.source_list?.length)
    ) {
      return (
        <div className="h-full w-full flex flex-col justify-center items-center">
          {!loading && <NoData />}
        </div>
      );
    }
  };

  return (
    <div className="h-full w-full">
      {loading ? (
        <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : (
        <>
          {checkNoData(userDetails)}
          {userDetails &&
            userDetails.files &&
            userDetails.files.source_list &&
            userDetails.files.source_list.length > 0 && (
              <div className="border-l rounded-md border-secondary ps-4 mb-4">
                <div className="pb-2 text-sm border-b border-primary inline-block pe-2">
                  Files
                </div>
                <div className="flex gap-2 w-full items-center">
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center mb-2">
                      <File size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.files?.total_files || 0}
                      </div>
                    </div>
                    <div className="text-xs text-primary">Total Files</div>
                  </div>
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center mb-2">
                      <Database size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {formatBandwidth(
                          userDetails?.files?.total_file_size || 0,
                          "B",
                          1,
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-primary">Total Size</div>
                  </div>
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center mb-2">
                      <AlertTriangle size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.files?.sensitive_file_count || 0}
                      </div>
                    </div>
                    <div className="text-xs text-primary">
                      Total sensitive Files
                    </div>
                  </div>
                </div>
              </div>
            )}

          {userDetails &&
            userDetails.emails &&
            userDetails.emails.source_list &&
            userDetails.emails.source_list.length > 0 && (
              <div className="border-l rounded-md border-secondary ps-4 mb-4">
                <div className="pb-2 text-sm border-b border-primary inline-block pe-2">
                  Emails
                </div>
                <div className="flex gap-2 w-full items-center">
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center">
                      <Mail size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.emails?.total_email_count || 0}
                      </div>
                    </div>
                    <span className="text-xs text-primary">
                      Total Sensitive Emails
                    </span>
                  </div>
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center">
                      <Paperclip size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.emails?.attachment_count || 0}
                      </div>
                    </div>
                    <span className="text-xs text-primary">
                      Total Attachments
                    </span>
                  </div>
                </div>
              </div>
            )}

          {userDetails &&
            userDetails.database &&
            userDetails.database.source_list &&
            userDetails.database.source_list.length > 0 && (
              <div className="border-l rounded-md border-secondary ps-4 mb-4">
                <div className="pb-2 text-sm border-b border-primary inline-block pe-2">
                  Databases
                </div>
                <div className="flex gap-2 w-full items-center">
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center">
                      <DatabaseIcon size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.database?.total_db || 0}
                      </div>
                    </div>
                    <span className="text-xs text-primary">Total Database</span>
                  </div>
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center">
                      <TriangleAlert size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.database?.sensitive_db_count || 0}
                      </div>
                    </div>
                    <span className="text-xs text-primary">
                      Total Sensitive Database
                    </span>
                  </div>
                </div>
              </div>
            )}

          {userDetails &&
            userDetails.conversations &&
            userDetails.conversations.source_list &&
            userDetails.conversations.source_list.length > 0 && (
              <div className="border-l rounded-md border-secondary ps-4 mb-4">
                <div className="pb-2 text-sm border-b border-primary inline-block pe-2">
                  Conversations
                </div>
                <div className="flex gap-2 w-full items-center">
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center">
                      <DatabaseIcon size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.conversations?.total_conversation_count ||
                          0}
                      </div>
                    </div>
                    <span className="text-xs text-primary">
                      Total Sensitive Conversations
                    </span>
                  </div>
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center">
                      <TriangleAlert size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.conversations?.attachment_count || 0}
                      </div>
                    </div>
                    <span className="text-xs text-primary">
                      Total Attachments
                    </span>
                  </div>
                </div>
              </div>
            )}

          {userDetails &&
            userDetails.generics &&
            userDetails.generics.source_list &&
            userDetails.generics.source_list.length > 0 && (
              <div className="border-l rounded-md border-secondary ps-4 mb-4">
                <div className="pb-2 text-sm border-b border-primary inline-block pe-2">
                  Generics
                </div>
                <div className="flex gap-2 w-full items-center">
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center">
                      <DatabaseIcon size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.generics?.sensitive_generic_count || 0}
                      </div>
                    </div>
                    <span className="text-xs text-primary">
                      Total Sensitive Generics
                    </span>
                  </div>
                  <div className="shadow-xl p-4 rounded-md">
                    <div className="flex gap-1 items-center">
                      <TriangleAlert size={20} className="text-primary" />
                      <div className="text-2xl font-bold">
                        {userDetails?.emails?.attachment_count || 0}
                      </div>
                    </div>
                    <span className="text-xs text-primary">
                      Total Attachments
                    </span>
                  </div>
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default Summary;
